import axios from 'axios';

// Get API URL from the global config or fallback to environment variables
const getApiUrl = () => {
  if (window.APP_CONFIG && window.APP_CONFIG.REACT_APP_API_URL) {
    return window.APP_CONFIG.REACT_APP_API_URL;
  }
  return process.env.REACT_APP_API_URL || '/api';
};

// Chunk size in bytes (5MB)
const CHUNK_SIZE = 5 * 1024 * 1024;
const MAX_RETRIES = 3;

const api = {
  /**
   * Generate a unique upload ID
   */
  generateUploadId() {
    return Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9);
  },

  /**
   * Upload a file directly (for small files)
   * @param {FormData} formData - Form data with file
   * @param {String} token - Authentication token
   * @param {Function} progressCallback - Progress callback function
   */
  async upload(formData, token, progressCallback = null) {
    const API_URL = getApiUrl();
    try {
      // Use XMLHttpRequest for progress tracking
      return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && progressCallback) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            progressCallback({
              status: 'uploading',
              progress: percentComplete
            });
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (error) {
              reject(new Error('Invalid JSON response'));
            }
          } else {
            reject(new Error(`HTTP error ${xhr.status}: ${xhr.statusText}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred'));
        });
        
        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });
        
        xhr.open('POST', `${API_URL}/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  },

  /**
   * Upload a single chunk with progress tracking
   * @param {Blob} chunk - File chunk
   * @param {Object} chunkInfo - Chunk metadata
   * @param {String} token - Authentication token
   * @param {Function} progressCallback - Progress callback function
   * @param {Number} retryCount - Current retry count
   */
  async uploadChunk(chunk, chunkInfo, token, progressCallback = null, retryCount = 0) {
    const API_URL = getApiUrl();
    const formData = new FormData();
    
    formData.append('file', chunk);
    formData.append('upload_id', chunkInfo.uploadId);
    formData.append('chunk_index', chunkInfo.index.toString());
    formData.append('total_chunks', chunkInfo.totalChunks.toString());
    formData.append('file_name', chunkInfo.fileName);
    
    try {
      // Use XMLHttpRequest for progress tracking within chunks
      return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && progressCallback) {
            // Calculate overall progress including this chunk's progress
            const chunkProgress = (event.loaded / event.total) * 100;
            const chunkContribution = 1 / chunkInfo.totalChunks;
            const overallPercentage = Math.min(
              100,
              Math.round(
                ((chunkInfo.index / chunkInfo.totalChunks) * 100) + 
                (chunkProgress * chunkContribution)
              )
            );
            
            progressCallback({
              status: 'uploading',
              progress: overallPercentage,
              currentChunk: chunkInfo.index + 1,
              totalChunks: chunkInfo.totalChunks,
              chunkProgress: Math.round(chunkProgress)
            });
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (error) {
              reject(new Error('Invalid JSON response'));
            }
          } else {
            reject(new Error(`HTTP error ${xhr.status}: ${xhr.statusText}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred'));
        });
        
        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });
        
        xhr.open('POST', `${API_URL}/upload/chunk`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });
    } catch (error) {
      // Retry logic
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying chunk ${chunkInfo.index} (attempt ${retryCount + 1})...`);
        // Exponential backoff
        const delay = Math.pow(2, retryCount) * 1000 + Math.random() * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.uploadChunk(chunk, chunkInfo, token, progressCallback, retryCount + 1);
      }
      
      console.error(`Error uploading chunk ${chunkInfo.index}:`, error);
      throw error;
    }
  },

  /**
   * Complete the chunked upload
   * @param {Object} uploadInfo - Upload information
   * @param {String} token - Authentication token
   */
  async completeUpload(uploadInfo, token) {
    const API_URL = getApiUrl();
    try {
      // Make sure we have the required properties
      if (!uploadInfo.uploadId) {
        throw new Error('Upload ID is required');
      }

      if (!uploadInfo.fileName) {
        throw new Error('File name is required');
      }

      const formData = new URLSearchParams();
      formData.append('upload_id', uploadInfo.uploadId);
      formData.append('file_name', uploadInfo.fileName);
      if (uploadInfo.options) {
        formData.append('options', JSON.stringify(uploadInfo.options));
      }

      console.log('Completing upload:', uploadInfo); // Debug log
      
      const response = await axios.post(`${API_URL}/upload/complete`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Complete upload error:', error);
      throw error;
    }
  },

  /**
   * Get upload status
   * @param {String} uploadId - Upload ID
   * @param {String} token - Authentication token
   */
  async getUploadStatus(uploadId, token) {
    const API_URL = getApiUrl();
    try {
      const formData = new URLSearchParams();
      formData.append('upload_id', uploadId);
      
      const response = await axios.post(`${API_URL}/upload/status`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Get upload status error:', error);
      throw error;
    }
  },
  
  /**
   * Get background job status
   * @param {String} jobId - Job ID
   * @param {String} token - Authentication token
   */
  async getJobStatus(jobId, token) {
    const API_URL = getApiUrl();
    try {
      const response = await axios.get(`${API_URL}/job/${jobId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Get job status error:', error);
      throw error;
    }
  },

  /**
   * Upload a file with chunking support
   * @param {File} file - The file to upload
   * @param {String} token - Authentication token
   * @param {Function} progressCallback - Progress callback function
   * @param {Object} options - Summary generation options
   */
  async uploadFile(file, token, progressCallback = null, options = null) {
    try {
      // For small files, use direct upload
      if (file.size <= CHUNK_SIZE) {
        const formData = new FormData();
        formData.append('file', file);
        if (options) {
          formData.append('options', JSON.stringify(options));
        }

        if (progressCallback) {
          progressCallback({
            status: 'uploading',
            progress: 0
          });
        }

        const result = await this.upload(formData, token, progressCallback);
        
        if (progressCallback) {
          progressCallback({
            status: 'completed',
            progress: 100
          });
        }
        
        return result;
      }
      
      // For large files, use chunked upload
      const uploadId = this.generateUploadId();
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const fileName = file.name; // Store the original filename
      
      if (progressCallback) {
        progressCallback({
          status: 'initializing',
          progress: 0,
          totalChunks
        });
      }
      
      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        // Check if upload was cancelled
        if (progressCallback && progressCallback().cancelled) {
          throw new Error('Upload cancelled by user');
        }
        
        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunk = file.slice(start, end);
        
        const chunkInfo = {
          uploadId,
          index: i,
          totalChunks,
          fileName: fileName // Ensure filename is included with each chunk
        };
        
        await this.uploadChunk(chunk, chunkInfo, token, progressCallback);
      }
      
      // Complete upload
      if (progressCallback) {
        progressCallback({
          status: 'finalizing',
          progress: 99
        });
      }
      
      console.log('About to complete upload with:', { uploadId, fileName, options }); // Debug log

      const result = await this.completeUpload({
        uploadId,
        fileName,
        options
      }, token);
      
      if (progressCallback) {
        progressCallback({
          status: 'completed',
          progress: 100
        });
      }
      
      return result;
    } catch (error) {
      if (progressCallback) {
        progressCallback({
          status: 'error',
          error: error.message
        });
      }
      
      throw error;
    }
  },

  /**
   * Get all jobs with filtering, search, pagination, and sorting
   * @param {String} status - Job status ('all', 'complete', 'in_progress', 'failed')
   * @param {String} token - Authentication token
   * @param {Object} options - Query options
   * @param {String} options.search - Search term for file name
   * @param {Number} options.page - Page number
   * @param {Number} options.pageSize - Results per page
   * @param {String} options.sortBy - Field to sort by
   * @param {String} options.sortOrder - Sort direction ('asc' or 'desc')
   * @param {String} options.startDate - Start date filter (ISO format)
   * @param {String} options.endDate - End date filter (ISO format)
   * @returns {Object} Jobs data with pagination information
   */
  async getJobs(status, token, options = {}) {
    const API_URL = getApiUrl();
    
    // Build query string from options
    const queryParams = new URLSearchParams();
    if (options.search) queryParams.append('search', options.search);
    if (options.page) queryParams.append('page', options.page);
    if (options.pageSize) queryParams.append('pageSize', options.pageSize);
    if (options.sortBy) queryParams.append('sortBy', options.sortBy);
    if (options.sortOrder) queryParams.append('sortOrder', options.sortOrder);
    if (options.startDate) queryParams.append('startDate', options.startDate);
    if (options.endDate) queryParams.append('endDate', options.endDate);
    
    const queryString = queryParams.toString();
    const url = `${API_URL}/jobs/${status}${queryString ? '?' + queryString : ''}`;
    
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  },

  /**
   * Download summary
   * @param {String} fileName - File name
   * @param {String} token - Authentication token
   */
  async downloadSummary(fileName, token) {
    const API_URL = getApiUrl();
    const encodedFileName = encodeURIComponent(fileName);
    const response = await axios.get(`${API_URL}/download/${encodedFileName}`, {
      headers: {
      Authorization: `Bearer ${token}`
      },
      responseType: 'blob'
    });
    
    // Create a download link and trigger it
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.docx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return response.data;
  },
  
  /**
   * Delete a summary
   * @param {String} jobUuid - Job UUID to delete
   * @param {String} token - Authentication token
   * @returns {Object} Delete operation result
   */
  async deleteSummary(jobUuid, token) {
    const API_URL = getApiUrl();
    const response = await axios.delete(`${API_URL}/jobs/${jobUuid}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
};

export default api;